import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Clock, MapPin, Minus, Plus, Ruler, Truck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useApi, money } from "../lib/api";
import { useSeo, breadcrumbLd } from "../lib/seo";
import { useCart } from "../context/CartContext";
import { Crumbs } from "../components/shop/Common";
import { ColorPicker, SizePicker, unavailableSizes, unavailableColors, firstColor } from "../components/shop/VariantPicker";
import { ProductCard, DraftBadge, SoldOutBadge } from "../components/shop/ProductCard";
import NotFoundBlock from "./NotFoundBlock";
import { variantPrice } from "../lib/categories";
import { FavoriteButton } from "../components/shop/FavoriteButton";

const SIZE_CHART = [["S", "18", "28"], ["M", "20", "29"], ["L", "22", "30"], ["XL", "24", "31"], ["2XL", "26", "32"], ["3XL", "28", "33"]];

const Gallery = ({ p, images }) => {
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [full, setFull] = useState(false);
  const img = images[i] || images[0];
  return (
    <div className="lg:sticky lg:top-28">
      <div
        className="relative aspect-square rounded-[32px] overflow-hidden bg-sand cursor-zoom-in"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setZoom(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
        }}
        onMouseLeave={() => setZoom(null)}
        onClick={() => setFull(true)}
        data-testid="product-main-image"
      >
        <img src={img} alt={`${p.name} product mockup`} className="zoom-img w-full h-full object-cover" style={zoom ? { transform: "scale(2)", transformOrigin: zoom } : undefined} />
        {p.status !== "active" && <DraftBadge className="absolute top-4 left-4" />}
        {p.status === "active" && p.sold_out && <SoldOutBadge className="absolute top-4 left-4" />}
        <div className="absolute top-4 right-4" onClick={(e) => e.stopPropagation()}><FavoriteButton slug={p.slug} /></div>
      </div>
      {images.length > 1 && (
        <div className="mt-4 flex gap-3 overflow-x-auto hide-scrollbar">
          {images.map((src, k) => (
            <button key={src} onClick={() => setI(k)} className={`shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 ${k === i ? "border-navy" : "border-transparent"}`} data-testid={`product-thumb-${k}`}>
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-4xl p-2 rounded-3xl" data-testid="product-zoom-dialog">
          <DialogTitle className="sr-only">{p.name}</DialogTitle>
          <img src={img} alt={p.name} className="w-full rounded-2xl" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ p }) => (
  <Accordion type="multiple" defaultValue={["desc"]} className="mt-8" data-testid="product-info-accordion">
    {[
      ["desc", "Description", p.description],
      ["material", "Material & Care", <><p>{p.material}</p><p className="mt-2">{p.care}</p></>],
      ["shipping", "Shipping & Production", "Each item is printed on demand after you order. Production typically takes 2–7 business days, then U.S. shipping usually takes 3–8 business days. These are estimates, not guarantees — you'll get tracking as soon as your order ships."],
      ["returns", "Returns & Refunds", <>Because items are made to order, we can't accept returns for change of mind or wrong size. Damaged or misprinted? We'll replace or refund it. <Link to="/policies/return-policy" className="underline">Read the full policy</Link>.</>],
    ].map(([v, t, body]) => (
      <AccordionItem key={v} value={v}>
        <AccordionTrigger className="text-navy font-bold text-base" data-testid={`accordion-${v}`}>{t}</AccordionTrigger>
        <AccordionContent className="text-navy/75 leading-relaxed">{body}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { data: p, loading, error } = useApi(`/products/${slug}`);
  const related = useApi(`/products/${slug}/related`);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    if (!p) return;
    setColor(firstColor(p));
    setSize(p.sizes?.length === 1 ? p.sizes[0] : null);
    setQty(1);
  }, [p]);

  useSeo({
    title: p ? `${p.name}` : undefined,
    description: p ? `${p.description.slice(0, 140)} Printed on demand. ${money(p.price)}.` : undefined,
    image: p?.images?.[0],
    noindex: p && p.status !== "active",
    jsonLd: p && {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Product", name: p.name, image: p.images, description: p.description, brand: { "@type": "Brand", name: "Vacation Apparel" },
          offers: { "@type": "Offer", priceCurrency: "USD", price: p.price, availability: p.sold_out ? "https://schema.org/OutOfStock" : "https://schema.org/MadeToOrder", url: window.location.href } },
        breadcrumbLd([["Home", "/"], ["Shop", "/shop"], [p.name, `/products/${p.slug}`]]),
      ],
    },
  });

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-16"><div className="aspect-square max-w-xl rounded-[32px] bg-sand animate-pulse" /></div>;
  if (error || !p) return <NotFoundBlock />;

  const draft = p.status !== "active";
  const soldOut = !!p.sold_out;
  const blocked = draft || soldOut || !size || unavailableSizes(p, color).includes(size);
  const doAdd = (buy) => {
    if (!size) return;
    if (add(p, color, size, qty) && buy) navigate("/checkout");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="product-page">
      <Crumbs items={[["Home", "/"], ["Shop", "/shop"], [p.name]]} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 [&>*]:min-w-0">
        <Gallery key={color || "all"} p={p} images={p.images_by_color?.[color]?.length ? p.images_by_color[color] : p.images} />
        <div>
          {draft && (
            <div className="mb-5 rounded-2xl bg-sunset/10 border border-sunset/30 p-4 text-sm text-navy" data-testid="draft-notice">
              {p.printify_product_id ? <><b>Draft (preview only).</b> This product is imported from Printify but not activated yet, so it can't be purchased.</> : <><b>Draft placeholder (preview only).</b> This product isn't imported from Printify yet and can't be purchased.</>}
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-navy tracking-tight" data-testid="product-name">{p.name}</h1>
          <p className="mt-3 text-2xl font-bold text-navy flex items-center gap-3" data-testid="product-price">{money(size ? variantPrice(p, color, size) : p.price)}{soldOut && <SoldOutBadge />}</p>
          {soldOut && <p className="mt-2 text-sm text-navy/70" data-testid="sold-out-notice">This design is temporarily sold out. Check back soon — availability updates automatically.</p>}
          <div className="mt-8 space-y-6">
            <ColorPicker colors={p.colors} unavailable={unavailableColors(p)} value={color} onChange={(c) => { setColor(c); if (unavailableSizes(p, c).includes(size)) setSize(null); }} />
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-navy">Size{size && <span className="font-normal text-navy/70">: {size}</span>}</p>
                {p.product_type !== "hat" && (
                  <button onClick={() => setGuide(true)} className="text-sm font-semibold text-[#0078a0] underline underline-offset-4 flex items-center gap-1" data-testid="size-guide-button">
                    <Ruler className="w-4 h-4" /> Size guide
                  </button>
                )}
              </div>
              <SizePicker sizes={p.sizes} value={size} onChange={setSize} unavailable={unavailableSizes(p, color)} />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center h-14 rounded-full border-2 border-border">
                <button className="w-12 h-full grid place-items-center" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-bold" data-testid="qty-value">{qty}</span>
                <button className="w-12 h-full grid place-items-center" onClick={() => setQty(Math.min(20, qty + 1))} aria-label="Increase quantity" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
              </div>
              <button disabled={blocked} onClick={() => doAdd(false)} className="flex-1 h-14 rounded-full bg-sun text-navy font-extrabold text-lg hover:bg-[#ffe34d] transition-colors disabled:opacity-40" data-testid="add-to-cart-button">
                {draft ? "Not for sale (draft)" : soldOut ? "Sold out" : size ? "Add to Cart" : "Select a size"}
              </button>
            </div>
            <button disabled={blocked} onClick={() => doAdd(true)} className="w-full h-14 rounded-full bg-navy text-white font-bold text-lg hover:bg-[#0c2c50] transition-colors disabled:opacity-40" data-testid="buy-now-button">
              Buy Now
            </button>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-2xl bg-sand p-4 flex gap-3"><Clock className="w-5 h-5 text-sunset shrink-0" /><span><b>Printed on demand</b><br />Production: ~2–7 business days</span></div>
            <div className="rounded-2xl bg-sand p-4 flex gap-3"><Truck className="w-5 h-5 text-turq shrink-0" /><span><b>U.S. shipping</b><br />Usually 3–8 business days after production</span></div>
          </div>
          {p.destination_links?.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="product-destinations">
              <span className="text-sm text-navy/60">Part of:</span>
              {p.destination_links.map((d) => (
                <Link key={d.slug} to={`/destinations/${d.slug}`} className="inline-flex items-center gap-1 rounded-full bg-turq/10 text-[#0078a0] font-semibold text-sm px-3 py-1.5 hover:bg-turq hover:text-white transition-colors" data-testid={`product-destination-link-${d.slug}`}>
                  <MapPin className="w-3.5 h-3.5" /> {d.name} Collection
                </Link>
              ))}
            </div>
          )}
          <Info p={p} />
        </div>
      </div>

      {related.data?.length > 0 && (
        <section className="mt-20" data-testid="related-products">
          <h2 className="text-3xl font-extrabold text-navy mb-8">You might also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
            {related.data.slice(0, 4).map((r) => <ProductCard key={r.slug} p={r} />)}
          </div>
        </section>
      )}

      <Dialog open={guide} onOpenChange={setGuide}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="size-guide-dialog">
          <DialogTitle className="font-brush text-3xl text-navy">Size guide</DialogTitle>
          <p className="text-sm text-navy/70">Measurements in inches, laid flat. Unisex fit. [Draft — will be replaced with exact Printify blank specs.]</p>
          <table className="w-full text-sm text-navy">
            <thead><tr className="text-left border-b border-border"><th className="py-2">Size</th><th>Chest width</th><th>Length</th></tr></thead>
            <tbody>{SIZE_CHART.map((r) => <tr key={r[0]} className="border-b border-border/60"><td className="py-2 font-bold">{r[0]}</td><td>{r[1]}"</td><td>{r[2]}"</td></tr>)}</tbody>
          </table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
